import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

interface BaseStripeCardInputProps {
  label?: string;
  withAsterisk?: boolean;
  disabled?: boolean;
  error?: string;
  icon: IconDefinition;
  children: React.ReactElement;
}

const BaseStripeCardInput: React.FC<BaseStripeCardInputProps> = ({
  label,
  withAsterisk = false,
  disabled = false,
  error,
  icon,
  children,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [iconWidth, setIconWidth] = useState(0);
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (iconRef.current) {
      setIconWidth(iconRef.current.offsetWidth);
    }
  }, []);

  // Clone the child element and add focus/blur handlers
  const childWithHandlers = React.cloneElement(children, {
    ...children.props,
    onFocus: () => {
      setIsFocused(true);
      children.props.onFocus?.();
    },
    onBlur: () => {
      setIsFocused(false);
      children.props.onBlur?.();
    },
  });

  return (
    <div className='w-full'>
      {label && (
        <label className='font-inter font-light text-[15px] text-real-black leading-6 mb-1 block'>
          {label}
          {withAsterisk && (
            <span className='text-real-red-500 text-sm ml-1'>*</span>
          )}
        </label>
      )}
      <div className='flex items-stretch relative'>
        <div
          className={classNames(
            'rounded-md w-full font-inter font-light bg-real-white py-[9px] cursor-pointer pr-4',
            'border border-solid border-real-grey-300 outline-none',
            'transition-colors',
            {
              'border-real-black': isFocused && !error,
              'border-real-red-500': !!error,
              'bg-real-grey-50 cursor-not-allowed': disabled,
              'bg-real-red-50': error && disabled,
            },
          )}
          style={{ paddingLeft: `${10 + iconWidth}px` }}
        >
          <div
            ref={iconRef}
            className={classNames(
              'absolute left-0 top-1/2 -translate-y-1/2 text-sm pl-4 z-10 flex',
              {
                'text-real-blue-500': !error && !disabled,
                'text-real-red-500': !!error,
                'text-real-grey-400': disabled,
              },
            )}
          >
            <FontAwesomeIcon icon={icon} />
          </div>
          {childWithHandlers}
        </div>
      </div>
      {error && (
        <p className='!text-real-red-500 !text-sm mt-1 font-inter font-light'>
          {error}
        </p>
      )}
    </div>
  );
};

export default BaseStripeCardInput;
